import type { AxiosResponse } from "axios";
import catalog from "@granular/fabric-catalog";

import { getAuthToken } from "./granularAxios";
import { axiosInstance, computeHeaders } from "./axios";
import { refinedListOfFields } from "./helpers/refinements";
import {
  BasicField,
  AddFieldRequest,
  FieldResponse,
  ResponseOfGetFields,
  FieldsNetworkResponse,
  DataForAddFieldNetworkRequest,
  FieldsSyncDataResponse,
  FieldsAttributesSyncDataNetworkResponse,
  FieldsRevisionsSyncDataNetworkResponse,
} from "../models/fields";

const BASE_URL = "/api/fields-svc";

/**
 * NOTE: We lowered this from 1000 to 100 to accommodate a small number of
 * operations (~30) that had very large response payloads that exceeded the
 * Field Service Lambda's 6MB limit resulting in a failed call. We want to
 * fetch as much data/fields as possible in a single call. A vast majority of
 * operations (~370,000 at the time of this writing) won't be affected by this
 * reduced limit.
 *
 * TODO: Longer-term solution to this.
 */
const DEFAULT_PAGE_SIZE = 100;

/**
 * TODO: Bagder my CAT team about how I want farms in this response
 * without making another call.
 */
const getFields = async (
  {
    authToken,
    userId,
    operationId,
    pageSize = DEFAULT_PAGE_SIZE,
    offset = 0,
    loadedItems = [],
  }: {
    authToken: string;
    userId: string;
    operationId: string;
    pageSize?: number;
    offset?: number;
    loadedItems?: FieldResponse[];
  },
  catalogFlag?: boolean,
): Promise<ResponseOfGetFields> => {
  let response;
  if (catalogFlag) {
    const catalogConfig = {
      // IMPORTANT: We are not passing the authToken as part of the queryKey
      // because we don't want to invalidate our data on auth token change.
      // The auth token can and will change throughout the lifetime of the app,
      // but that should not invalidate our data.
      getAuthToken,
      getOperationId: () => operationId,
    };
    response = await catalog.fields.v3.getFields(
      { limit: pageSize, offset },
      catalogConfig,
    );

    if (response.data.data?.length) {
      loadedItems = loadedItems.concat(response.data.data);

      if (response.data.meta.count > Math.max(loadedItems.length, offset)) {
        return getFields(
          {
            authToken,
            userId,
            operationId,
            pageSize,
            offset:
              response.data.meta.offset + response.data.meta.returned_count,
            loadedItems,
          },
          catalogFlag,
        );
      }
    }
  } else {
    response = await axiosInstance.request<
      never,
      AxiosResponse<FieldsNetworkResponse>
    >({
      method: "GET",
      url: `${BASE_URL}/v2/field/historical?limit=${pageSize}&offset=${offset}`,
      headers: computeHeaders({
        authToken,
        userId,
        operationId,
      }),
    });

    if (response.data.fields?.length) {
      loadedItems = loadedItems.concat(response.data.fields);

      if (
        response.data._metadata.count > Math.max(loadedItems.length, offset)
      ) {
        return getFields(
          {
            authToken,
            userId,
            operationId,
            pageSize,
            offset: response.data._metadata.offset + pageSize,
            loadedItems,
          },
          catalogFlag,
        );
      }
    }
  }

  return { fields: loadedItems, count: loadedItems.length };
};

const getFieldsSyncData = async (
  {
    authToken,
    userId,
    operationId,
  }: {
    authToken: string;
    userId: string;
    operationId: string;
  },
  catalogFlag?: boolean,
): Promise<FieldsSyncDataResponse> => {
  const attributesPromise = catalogFlag
    ? catalog.fields.syncFieldAttributes({
        // IMPORTANT: We are not passing the authToken as part of the queryKey
        // because we don't want to invalidate our data on auth token change.
        // The auth token can and will change throughout the lifetime of the app,
        // but that should not invalidate our data.
        getAuthToken,
        getOperationId: () => operationId,
      })
    : axiosInstance.request<
        never,
        AxiosResponse<FieldsAttributesSyncDataNetworkResponse>
      >({
        method: "GET",
        url: `${BASE_URL}/v0/field-attribute/sync-keys`,
        headers: computeHeaders({
          authToken,
          userId,
          operationId,
        }),
      });
  const revisionsPromise = catalogFlag
    ? catalog.fields.syncFieldRevisions({
        // IMPORTANT: We are not passing the authToken as part of the queryKey
        // because we don't want to invalidate our data on auth token change.
        // The auth token can and will change throughout the lifetime of the app,
        // but that should not invalidate our data.
        getAuthToken,
        getOperationId: () => operationId,
      })
    : axiosInstance.request<
        never,
        AxiosResponse<FieldsRevisionsSyncDataNetworkResponse>
      >({
        method: "GET",
        url: `${BASE_URL}/v0/field-revision/sync-keys`,
        headers: computeHeaders({
          authToken,
          userId,
          operationId,
        }),
      });

  const [attributesResponse, revisionsResponse] = await Promise.all([
    attributesPromise,
    revisionsPromise,
  ]);

  return {
    attributes: {
      active: attributesResponse.data.active,
    },
    revisions: { active: revisionsResponse.data.active },
  };
};

const removeField = async (
  {
    authToken,
    userId,
    operationId,
    fieldId,
  }: {
    authToken: string;
    userId: string;
    operationId: string;
    fieldId: string;
  },
  catalogFlag?: boolean,
): Promise<null> => {
  let response;
  if (catalogFlag) {
    const catalogConfig = {
      // IMPORTANT: We are not passing the authToken as part of the queryKey
      // because we don't want to invalidate our data on auth token change.
      // The auth token can and will change throughout the lifetime of the app,
      // but that should not invalidate our data.
      getAuthToken,
      getOperationId: () => operationId,
    };
    response = await catalog.fields.deleteField(fieldId, catalogConfig);
  } else {
    response = await axiosInstance.request({
      method: "DELETE",
      url: `${BASE_URL}/v2/field/${fieldId}`,
      headers: computeHeaders({
        authToken,
        userId,
        operationId,
      }),
    });
  }

  return response.data;
};

const addField = async (
  {
    authToken,
    userId,
    operationId,
    field,
  }: {
    authToken: string;
    userId: string;
    operationId: string;
    field: AddFieldRequest;
  },
  catalogFlag?: boolean,
): Promise<BasicField> => {
  let response;
  if (catalogFlag) {
    const catalogConfig = {
      // IMPORTANT: We are not passing the authToken as part of the queryKey
      // because we don't want to invalidate our data on auth token change.
      // The auth token can and will change throughout the lifetime of the app,
      // but that should not invalidate our data.
      getAuthToken,
      getOperationId: () => operationId,
    };
    response = await catalog.fields.addField(
      {
        field_id: field.id,
        name: field.name,
        farm_id: field.farmId,
        shape: field.shape,
        thumbnail_svg: field.thumbnailSvg,
        end: field.end,
        revision_id: field.revisionId,
        source: field.source,
        start: field.start,
        zone_id: field.zoneId,
      },
      catalogConfig,
    );
  } else {
    response = await axiosInstance.request<
      DataForAddFieldNetworkRequest,
      AxiosResponse<FieldResponse>
    >({
      method: "POST",
      url: `${BASE_URL}/v2/field`,
      headers: computeHeaders({
        authToken,
        userId,
        operationId,
      }),
      data: {
        field_id: field.id,
        name: field.name,
        farm_id: field.farmId,
        shape: field.shape,
        thumbnail_svg: field.thumbnailSvg,
        end: field.end,
        revision_id: field.revisionId,
        source: field.source,
        start: field.start,
        zone_id: field.zoneId,
      },
    });
  }

  return refinedListOfFields([response.data])[0];
};

export default {
  getFields,
  getFieldsSyncData,
  removeField,
  addField,
};
